import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Loader2, X } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { useCreateCreditNote } from '@/hooks/useInvoices'
import { getApiErrorMessage, getApiFieldError } from '@/services/api'
import { cn } from '@/lib/utils'
import {
  formatCurrency,
  formatCurrencyInputValue,
  parseCurrencyInput,
  roundCurrencyAmount,
} from '@/lib/format'
import {
  CREDIT_NOTE_REASON_LABELS,
  CREDIT_NOTE_REASON_OPTIONS,
  type CreditNote,
} from '@/types'

interface Props {
  open: boolean
  onOpenChange: (o: boolean) => void
  // Pre-selected invoice (when launched from an invoice detail screen).
  invoiceId: string
  clientId: string
  clientName?: string | null
  invoiceNumber?: string | null
  balanceDue?: number
  paidAmount?: number
  maxCreditAmount?: number
  existingCreditNotes?: CreditNote[]
  onCreated?: () => void
}

const panelCardClass =
  'w-full rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
const fieldControlClass =
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#6c7285] focus-visible:ring-0 focus-visible:ring-offset-0'
const fieldErrorClass = 'border-[#d63b33] text-[#171a20]'

// Field label with an optional red required marker, matching Figma 1125:11522.
function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#ec221f]'>*</span>}
    </Label>
  )
}

function SummaryAmount({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: string
  tone?: 'default' | 'brand' | 'danger'
}) {
  return (
    <div className='flex min-w-[160px] flex-1 flex-col items-center gap-2 text-center'>
      <p className='text-xs font-medium uppercase leading-[1.2] text-[#5a5a5a]'>
        {label}
      </p>
      <p
        className={cn(
          'text-xl font-bold leading-[1.2]',
          tone === 'brand' && 'text-[#ff5a1f]',
          tone === 'danger' && 'text-[#991b1b]',
          tone === 'default' && 'text-[#171a20]'
        )}
      >
        {value}
      </p>
    </div>
  )
}

function ReasonLabel({ reason }: { reason: string | null | undefined }) {
  return <>{reason ? (CREDIT_NOTE_REASON_LABELS[reason] ?? reason) : '-'}</>
}

// 2.6.10 Create credit note - linked invoice, amount, reason, description.
export function CreateCreditNoteDialog({
  open,
  onOpenChange,
  invoiceId,
  clientId,
  clientName,
  invoiceNumber,
  balanceDue,
  paidAmount = 0,
  maxCreditAmount,
  existingCreditNotes = [],
  onCreated,
}: Props) {
  const { t } = useTranslation('billing')
  const create = useCreateCreditNote()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<{ amount?: string; reason?: string }>({})
  const wasOpenRef = useRef(false)
  const parsedAmount = parseCurrencyInput(amount)
  const safeBalanceDue = Math.max(balanceDue ?? 0, 0)
  const safeMaxCreditAmount = Math.max(maxCreditAmount ?? safeBalanceDue, 0)
  const isRefundCredit =
    balanceDue != null && safeBalanceDue === 0 && paidAmount > 0
  const amountOptionDisabled = balanceDue == null || maxCreditAmount == null
  const amountExceedsLimit =
    maxCreditAmount != null &&
    parsedAmount != null &&
    roundCurrencyAmount(parsedAmount - safeMaxCreditAmount) > 0
  const amountErrorMessage =
    errors.amount ??
    (amountExceedsLimit
      ? t(
          isRefundCredit
            ? 'createCreditNote.amountExceedsTotal'
            : 'createCreditNote.amountExceedsBalanceDue'
        )
      : '')
  const amountHasError = Boolean(amountErrorMessage)
  const afterCreditAmount = isRefundCredit
    ? roundCurrencyAmount(Math.max(parsedAmount ?? 0, 0))
    : parsedAmount == null
      ? safeBalanceDue
      : roundCurrencyAmount(Math.max(safeBalanceDue - parsedAmount, 0))
  const afterCreditLabel =
    amountHasError && maxCreditAmount != null
      ? t('createCreditNote.maxAmount', {
          amount: formatCurrency(safeMaxCreditAmount, 'EUR', 2),
        })
      : formatCurrency(afterCreditAmount, 'EUR', 2)
  const clientDisplayName =
    clientName?.trim() || t('createCreditNote.clientFallback')
  const invoiceDisplayNumber = invoiceNumber ?? invoiceId
  const amountPlaceholder =
    maxCreditAmount != null
      ? t('createCreditNote.maxAmount', {
          amount: formatCurrencyInputValue(safeMaxCreditAmount),
        })
      : '0,00'

  const applyAmountOption = (ratio: number) => {
    if (maxCreditAmount == null) return

    setAmount(
      formatCurrencyInputValue(roundCurrencyAmount(safeMaxCreditAmount * ratio))
    )
    setErrors(s => ({ ...s, amount: undefined }))
  }

  const amountOptionActive = (ratio: number) => {
    if (
      maxCreditAmount == null ||
      safeMaxCreditAmount <= 0 ||
      parsedAmount == null
    ) {
      return false
    }
    return (
      roundCurrencyAmount(parsedAmount) ===
      roundCurrencyAmount(safeMaxCreditAmount * ratio)
    )
  }

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Reset the form fields when the dialog opens.
      setAmount(
        balanceDue != null
          ? formatCurrencyInputValue(Math.max(balanceDue, 0))
          : ''
      )
      setReason('')
      setDescription('')
      setErrors({})
    }
    wasOpenRef.current = open
  }, [open, balanceDue])

  const submit = async () => {
    const next: typeof errors = {}
    const amt = parseCurrencyInput(amount)
    if (amt === null || amt <= 0)
      next.amount = t('createCreditNote.amountInvalid')
    else if (
      maxCreditAmount != null &&
      roundCurrencyAmount(amt - safeMaxCreditAmount) > 0
    )
      next.amount = t(
        isRefundCredit
          ? 'createCreditNote.amountExceedsTotal'
          : 'createCreditNote.amountExceedsBalanceDue'
      )
    if (!reason) next.reason = t('createCreditNote.reasonRequired')
    setErrors(next)
    if (Object.keys(next).length) return
    // Narrowed: validation above guarantees a positive amount.
    const creditAmount = amt as number

    try {
      // Feedback #63: the BE computes totals from items[] and ignores a
      // top-level `total`. Send a single line so the computed total equals the
      // entered amount; fold the optional description into the line description.
      await create.mutateAsync({
        invoice_id: invoiceId,
        client_id: clientId,
        reason,
        items: [
          {
            description: description?.trim()
              ? description.trim()
              : (CREDIT_NOTE_REASON_LABELS[reason] ?? reason),
            quantity: 1,
            unit_price: creditAmount,
            tax_rate: 0,
            line_total: creditAmount,
          },
        ],
      })
      toast({ variant: 'success', title: t('createCreditNote.success') })
      onOpenChange(false)
      onCreated?.()
    } catch (e) {
      const amountError = getApiFieldError(e, 'amount')
      if (amountError) {
        setErrors(s => ({ ...s, amount: amountError }))
      } else {
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('createCreditNote.error')),
        })
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        side='right'
        overlayClassName='bg-black/60'
        className='tf-right-drawer-short gap-0 shadow-[-16px_0_32px_-4px_rgba(75,80,95,0.1),-4px_0_4px_-4px_rgba(75,80,95,0.05)] [&>button]:hidden'
      >
        <div className='flex min-h-0 flex-1 flex-col'>
          <div className='tf-right-drawer-header tf-right-drawer-header-accent'>
            <DialogTitle className='min-w-0 flex-1 text-base font-semibold leading-[1.2] text-[#171a20]'>
              {t('createCreditNote.title')}
            </DialogTitle>
            <DialogClose className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#f4f5f8] text-[#171a20] transition-colors hover:bg-[#e7e9ef] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'>
              <X className='size-4' />
              <span className='sr-only'>{t('createCreditNote.cancel')}</span>
            </DialogClose>
          </div>

          <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[#f4f5f8] p-6'>
            <div className={cn(panelCardClass, 'flex flex-col gap-4 p-6')}>
              <div className='flex flex-col gap-0.5'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h3 className='text-base font-semibold leading-[1.2] text-[#171a20]'>
                    {clientDisplayName}
                  </h3>
                </div>
                <p className='text-xs font-normal leading-[1.4] text-[#3d434e]'>
                  {t('createCreditNote.onInvoice')}{' '}
                  <span className='font-medium'>{invoiceDisplayNumber}</span>
                </p>
              </div>

              <div className='flex flex-col items-center justify-center gap-4 rounded-2xl bg-[#fff1ea] p-4 shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)] sm:flex-row'>
                <SummaryAmount
                  label={t(
                    paidAmount > 0
                      ? 'createCreditNote.remainingBalance'
                      : 'createCreditNote.currentBalance'
                  )}
                  value={formatCurrency(safeBalanceDue, 'EUR', 2)}
                />
                <ArrowRight
                  className='size-5 shrink-0 text-[#ff5a1f]'
                  strokeWidth={2.2}
                />
                <SummaryAmount
                  label={t(
                    isRefundCredit
                      ? 'createCreditNote.toBeRefunded'
                      : 'createCreditNote.afterCredit'
                  )}
                  value={afterCreditLabel}
                  tone={amountHasError ? 'danger' : 'brand'}
                />
              </div>
            </div>

            <div className={cn(panelCardClass, 'p-4')}>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-1'>
                  <FieldLabel htmlFor='cn-amount' required>
                    {t('createCreditNote.amount')}
                  </FieldLabel>
                  <CurrencyInput
                    id='cn-amount'
                    value={amount}
                    showEuroPrefix
                    placeholder={amountPlaceholder}
                    onValueChange={raw => {
                      setAmount(raw)
                      setErrors(s => ({ ...s, amount: undefined }))
                    }}
                    className={cn(
                      fieldControlClass,
                      'pl-9 font-normal',
                      FORM_CONTROL_FOCUS_CLASS,
                      amountHasError && fieldErrorClass
                    )}
                    prefixClassName='text-sm font-normal text-[#171a20]'
                  />
                  {amountErrorMessage && (
                    <p className='text-xs font-medium leading-4 text-[#991b1b]'>
                      {amountErrorMessage}
                    </p>
                  )}
                </div>

                <div className='flex flex-col gap-1'>
                  <FieldLabel required>
                    {t('createCreditNote.reason')}
                  </FieldLabel>
                  <Select
                    value={reason}
                    onValueChange={v => {
                      setReason(v)
                      setErrors(s => ({ ...s, reason: undefined }))
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        fieldControlClass,
                        'pl-4 pr-3 font-normal [&_svg]:opacity-100',
                        errors.reason && fieldErrorClass
                      )}
                    >
                      <SelectValue
                        placeholder={t('createCreditNote.reasonPlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent className='rounded-lg border-[#e7e9ef] bg-white p-2 text-sm font-normal text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]'>
                      {CREDIT_NOTE_REASON_OPTIONS.map(k => (
                        <SelectItem
                          key={k}
                          value={k}
                          className='rounded-md py-1.5 pl-2 pr-2 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] [&>span:first-child]:hidden'
                        >
                          {CREDIT_NOTE_REASON_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.reason && (
                    <p className='text-xs font-medium leading-4 text-[#991b1b]'>
                      {errors.reason}
                    </p>
                  )}
                </div>

                <div className='flex flex-wrap gap-2'>
                  {[
                    {
                      ratio: 1,
                      label: t('createCreditNote.options.entire'),
                    },
                    { ratio: 0.5, label: t('createCreditNote.options.half') },
                    {
                      ratio: 0.1,
                      label: t('createCreditNote.options.tenPercent'),
                    },
                  ].map(option => {
                    const active = amountOptionActive(option.ratio)

                    return (
                      <Button
                        key={option.ratio}
                        type='button'
                        variant='outline'
                        disabled={amountOptionDisabled}
                        className={cn(
                          'h-auto rounded-xl border-[#e7e9ef] bg-white px-4 py-2.5 text-xs font-normal leading-[1.4] text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)] hover:bg-[#f4f5f8]',
                          active &&
                            'border-[#059669] bg-[#ecfdf5] hover:bg-[#ecfdf5]'
                        )}
                        onClick={() => applyAmountOption(option.ratio)}
                      >
                        {option.label}
                      </Button>
                    )
                  })}
                </div>

                <div className='flex flex-col gap-1'>
                  <FieldLabel htmlFor='cn-desc'>
                    {t('createCreditNote.notes')}
                  </FieldLabel>
                  <Textarea
                    id='cn-desc'
                    value={description}
                    maxLength={1000}
                    rows={3}
                    placeholder={t('createCreditNote.notesPlaceholder')}
                    className={cn(
                      'min-h-20 resize-y rounded-xl border-transparent bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
                      FORM_CONTROL_FOCUS_CLASS
                    )}
                    onChange={e => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            className={cn(
              'flex shrink-0 flex-col gap-4 border-t border-[#d9e2ec] bg-white px-6 py-6',
              existingCreditNotes.length > 0
                ? 'h-[162px]'
                : 'h-[72px] justify-center'
            )}
          >
            <div className='flex flex-wrap items-center justify-end gap-3'>
              <Button
                type='button'
                variant='outline'
                className='h-auto rounded-xl border-[#e7e9ef] bg-white p-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f4f5f8]'
                disabled={create.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t('createCreditNote.cancel')}
              </Button>
              <Button
                type='button'
                className='h-auto rounded-xl border border-primary bg-primary p-3 text-sm font-medium leading-none text-white hover:bg-primary/90'
                disabled={create.isPending}
                onClick={submit}
              >
                {create.isPending && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                {t('createCreditNote.issue')}
              </Button>
            </div>

            {existingCreditNotes.length > 0 && (
              <div
                className={cn(
                  'flex min-h-0 flex-col gap-2 border-t border-dashed border-[#b0b5c3] pt-4',
                  existingCreditNotes.length > 1
                    ? 'overflow-y-auto'
                    : 'overflow-hidden'
                )}
              >
                <p className='text-xs font-medium uppercase leading-[1.4] text-[#3d434e]'>
                  {t('createCreditNote.alreadyIssued')}
                </p>
                {existingCreditNotes.map(cn => (
                  <div
                    key={cn.id}
                    className='flex w-full min-w-0 items-center gap-1 text-xs font-normal leading-[1.4] text-[#171a20]'
                  >
                    <span className='shrink-0 whitespace-nowrap'>
                      {cn.number ?? t('createCreditNote.autoGenerated')}
                    </span>
                    <span className='size-1 shrink-0 rounded-full bg-[#171a20]' />
                    <span className='min-w-0 flex-1 truncate'>
                      <ReasonLabel reason={cn.reason} />
                    </span>
                    <span className='shrink-0 whitespace-nowrap text-sm font-medium'>
                      - {formatCurrency(cn.total, 'EUR', 2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
